import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { getProductImageUrl } from '../../../utils/productImage';

const ProductGallery = ({ product }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);

  const mainImage = product?.mainImage ?? product?.main_image;
  const galleryList = product?.galleryImages ?? product?.gallery_images;
  const galleryArray = Array.isArray(galleryList) ? galleryList : [];
  const rawImages = [mainImage, ...galleryArray].filter(Boolean);
  const resolved = rawImages.map((src) => getProductImageUrl(src)).filter(Boolean);
  const images = resolved.length ? resolved : [];

  const handlePrevious = () => {
    setSelectedImageIndex((prev) => 
      prev === 0 ? images?.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setSelectedImageIndex((prev) => 
      prev === images?.length - 1 ? 0 : prev + 1
    );
  };

  const handleThumbnailClick = (index) => {
    setSelectedImageIndex(index);
  };

  const toggleZoom = () => {
    setIsZoomed(!isZoomed);
  };

  return (
    <div className="space-y-4">
      {/* Main Image Display */}
      <div className="relative bg-white rounded-lg border border-border overflow-hidden group">
        <div className="aspect-[4/3] relative flex items-center justify-center">
          <AnimatePresence mode="wait">
            {images?.length > 0 ? (
              <motion.div
                key={selectedImageIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0"
              >
                <Image
                  src={images[selectedImageIndex]}
                  alt={`${product?.name} - View ${selectedImageIndex + 1}`}
                  className={`w-full h-full object-contain cursor-zoom-in transition-transform duration-300 ${
                    isZoomed ? 'scale-150' : 'scale-100'
                  }`}
                  onClick={toggleZoom}
                  decoding="async"
                />
              </motion.div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-muted text-text-secondary text-sm">
                No image
              </div>
            )}
          </AnimatePresence>

          {/* Navigation Arrows */}
          {images?.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-medical flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                aria-label="Previous image"
              >
                <Icon name="ChevronLeft" size={20} />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-medical flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                aria-label="Next image"
              >
                <Icon name="ChevronRight" size={20} />
              </button>
            </>
          )}

          {/* Zoom Icon */}
          <button
            onClick={toggleZoom}
            className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-medical flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            aria-label={isZoomed ? "Zoom out" : "Zoom in"}
          >
            <Icon name={isZoomed ? "ZoomOut" : "ZoomIn"} size={18} />
          </button>

          {/* Image Counter */}
          {images?.length > 1 && (
            <div className="absolute bottom-4 left-4 px-3 py-1 bg-black/70 text-white text-sm rounded-full">
              {selectedImageIndex + 1} / {images?.length}
            </div>
          )}
        </div>
      </div>
      {/* Thumbnail Gallery */}
      {images?.length > 1 && (
        <div className="grid grid-cols-4 gap-2">
          {images?.map((image, index) => (
            <button
              key={index}
              onClick={() => handleThumbnailClick(index)}
              className={`aspect-square rounded-lg border-2 overflow-hidden transition-all duration-200 ${
                index === selectedImageIndex
                  ? 'border-primary shadow-medical'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <Image
                src={image}
                alt={`${product?.name} thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}
      {/* Legacy 'Key Features' section removed to avoid duplication with main product view */}
    </div>
  );
};

export default ProductGallery;